import { useState, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import {
	BsPerson,
	BsEnvelope,
	BsPhone,
	BsBriefcase,
	BsBuilding,
	BsGlobe,
	BsGeoAlt,
	BsInfoCircle,
} from 'react-icons/bs';

import { updateContact, updateCompany } from '#crmFeature/api/crmApi';
import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import Tabs from '#globalComponents/Tabs/Tabs';

const TABS = { contact: 'contact', company: 'company' };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactListEditModal = ({
	isOpen,
	onClose,
	contact,
	company,
	initialTab = 'contact',
	companyContactCount = 0,
	onSaveSuccess,
}) => {
	const hasContact = Boolean(contact?.id);
	const hasCompany = Boolean(company?.id);

	const [activeTab, setActiveTab] = useState(initialTab);

	const [contactForm, setContactForm] = useState({
		firstName: '',
		lastName: '',
		emailAddress: '',
		phoneNumber: '',
		companyRole: '',
	});

	const [companyForm, setCompanyForm] = useState({
		companyName: '',
		emailAddress: '',
		websiteUrl: '',
		phoneNumber: '',
		addressStreet: '',
		addressPostal: '',
		addressCity: '',
	});

	const [emailError, setEmailError] = useState('');
	const [companyEmailError, setCompanyEmailError] = useState('');

	// Reset tab when modal opens with a new initialTab. Company recipients have no contact,
	// so fall back to the company tab rather than opening a tab that is not rendered.
	useEffect(() => {
		if (!isOpen) return;
		if (initialTab === TABS.contact && !hasContact) setActiveTab(TABS.company);
		else setActiveTab(initialTab);
	}, [isOpen, initialTab, hasContact]);

	useEffect(() => {
		if (contact) {
			setContactForm({
				firstName: contact.firstName || '',
				lastName: contact.lastName || '',
				emailAddress: contact.emailAddress || '',
				phoneNumber: contact.phoneNumber || '',
				companyRole: contact.companyRole || '',
			});
			setEmailError('');
		}
	}, [contact]);

	useEffect(() => {
		if (company) {
			setCompanyForm({
				companyName: company.companyName || '',
				emailAddress: company.emailAddress || '',
				websiteUrl: company.websiteUrl || '',
				phoneNumber: company.phoneNumber || '',
				addressStreet: company.addressStreet || '',
				addressPostal: company.addressPostal || '',
				addressCity: company.addressCity || '',
			});
			setCompanyEmailError('');
		}
	}, [company]);

	const contactMutation = useMutation({
		mutationFn: (payload) => updateContact(contact.id, payload),
		onSuccess: () => {
			onSaveSuccess?.();
			onClose();
		},
	});

	const companyMutation = useMutation({
		mutationFn: (payload) => updateCompany(company.id, payload),
		onSuccess: () => {
			onSaveSuccess?.();
			onClose();
		},
	});

	const handleContactChange = (e) => {
		const { name, value } = e.target;
		setContactForm((prev) => ({ ...prev, [name]: value }));
		if (name === 'emailAddress') setEmailError('');
	};

	const handleCompanyChange = (e) => {
		const { name, value } = e.target;
		setCompanyForm((prev) => ({ ...prev, [name]: value }));
		if (name === 'emailAddress') setCompanyEmailError('');
	};

	const handleSave = async () => {
		if (activeTab === TABS.contact) {
			if (contactForm.emailAddress.trim() && !EMAIL_REGEX.test(contactForm.emailAddress.trim())) {
				setEmailError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
				return;
			}
			await contactMutation.mutateAsync(contactForm);
		} else {
			if (companyForm.emailAddress.trim() && !EMAIL_REGEX.test(companyForm.emailAddress.trim())) {
				setCompanyEmailError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
				return;
			}
			await companyMutation.mutateAsync(companyForm);
		}
	};

	const isSaving = contactMutation.isPending || companyMutation.isPending;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="m">
			<Panel.Header
				onClose={onClose}
				subtitle={hasContact && company?.companyName ? company.companyName : undefined}
			>
				{hasContact ? contact.fullName || 'Kontakt bearbeiten' : company?.companyName || 'Firma bearbeiten'}
			</Panel.Header>

			{hasContact && hasCompany && (
				<Tabs
					tabs={[
						{ id: TABS.contact, label: 'Kontakt' },
						{ id: TABS.company, label: 'Unternehmen' },
					]}
					activeId={activeTab}
					onChange={setActiveTab}
					className="flex-shrink-0 px-5"
				/>
			)}

			<Panel.Body className="space-y-4">
				{activeTab === TABS.contact && hasContact && (
					<>
						<Field
							id="firstName"
							label="Vorname"
							icon={BsPerson}
							value={contactForm.firstName}
							onChange={handleContactChange}
							placeholder="Max"
						/>
						<Field
							id="lastName"
							label="Nachname"
							icon={BsPerson}
							value={contactForm.lastName}
							onChange={handleContactChange}
							placeholder="Mustermann"
						/>
						<Field
							id="emailAddress"
							label="E-Mail-Adresse"
							icon={BsEnvelope}
							type="email"
							value={contactForm.emailAddress}
							onChange={handleContactChange}
							placeholder="max@beispiel.de"
							error={emailError}
						/>
						<Field
							id="phoneNumber"
							label="Telefonnummer"
							icon={BsPhone}
							type="tel"
							value={contactForm.phoneNumber}
							onChange={handleContactChange}
							placeholder="+49 123 456789"
						/>
						<Field
							id="companyRole"
							label="Position"
							icon={BsBriefcase}
							value={contactForm.companyRole}
							onChange={handleContactChange}
							placeholder="Geschäftsführer"
						/>
					</>
				)}

				{activeTab === TABS.company && hasCompany && (
					<>
						{companyContactCount > 1 && (
							<div className="flex items-start gap-2 px-3 py-2.5 bg-primary-100 border border-primary-200 rounded-lg">
								<BsInfoCircle className="w-3.5 h-3.5 text-primary-500 mt-0.5 flex-shrink-0" />
								<p className="text-[11px] text-primary-700 leading-relaxed">
									Änderungen gelten für alle <strong>{companyContactCount} Kontakte</strong> von{' '}
									<strong>{company.companyName}</strong> in dieser Liste.
								</p>
							</div>
						)}

						<Field
							id="companyName"
							label="Firmenname"
							icon={BsBuilding}
							value={companyForm.companyName}
							onChange={handleCompanyChange}
							placeholder="Firmenname eingeben"
						/>
						{/* The send address for company recipients that have no contact person */}
						<Field
							id="emailAddress"
							label="E-Mail-Adresse"
							icon={BsEnvelope}
							type="email"
							value={companyForm.emailAddress}
							onChange={handleCompanyChange}
							placeholder="info@beispiel.de"
							error={companyEmailError}
						/>
						<Field
							id="websiteUrl"
							label="Website"
							icon={BsGlobe}
							type="url"
							value={companyForm.websiteUrl}
							onChange={handleCompanyChange}
							placeholder="https://www.beispiel.de"
						/>
						<Field
							id="phoneNumber"
							label="Telefonnummer"
							icon={BsPhone}
							type="tel"
							value={companyForm.phoneNumber}
							onChange={handleCompanyChange}
							placeholder="+49 123 456789"
						/>
						<div className="pt-1">
							<div className="flex items-center gap-2 text-[13px] font-medium text-text mb-3">
								<BsGeoAlt className="w-3.5 h-3.5 text-primary-600" />
								<span>Adresse</span>
							</div>
							<div className="space-y-3">
								<Field
									id="addressStreet"
									label="Straße und Hausnummer"
									value={companyForm.addressStreet}
									onChange={handleCompanyChange}
									placeholder="Musterstraße 123"
									compact
								/>
								<div className="grid grid-cols-[120px_1fr] gap-3">
									<Field
										id="addressPostal"
										label="PLZ"
										value={companyForm.addressPostal}
										onChange={handleCompanyChange}
										placeholder="12345"
										compact
									/>
									<Field
										id="addressCity"
										label="Stadt"
										value={companyForm.addressCity}
										onChange={handleCompanyChange}
										placeholder="Berlin"
										compact
									/>
								</div>
							</div>
						</div>
					</>
				)}
			</Panel.Body>

			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
					Abbrechen
				</Button>
				<Button
					type="button"
					variant="solid"
					size="sm"
					onClick={handleSave}
					loading={isSaving}
					disabled={isSaving}
				>
					Speichern
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

const Field = ({ id, label, icon: Icon, type = 'text', value, onChange, placeholder, error, compact }) => (
	<div>
		{!compact && (
			<label htmlFor={id} className="flex items-center gap-1.5 text-[13px] font-medium text-text mb-1.5">
				{Icon && <Icon className="w-3.5 h-3.5 text-primary-600" />}
				{label}
			</label>
		)}
		{compact && (
			<label htmlFor={id} className="block text-[12px] font-medium text-text mb-1">
				{label}
			</label>
		)}
		<TextInput
			type={type}
			id={id}
			name={id}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			error={Boolean(error)}
		/>
		{error && <p className="text-[11px] text-warning-500 mt-1">{error}</p>}
	</div>
);

Field.propTypes = {
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	icon: PropTypes.elementType,
	type: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	error: PropTypes.string,
	compact: PropTypes.bool,
};

ContactListEditModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	contact: PropTypes.object,
	company: PropTypes.object,
	initialTab: PropTypes.oneOf(['contact', 'company']),
	companyContactCount: PropTypes.number,
	onSaveSuccess: PropTypes.func,
};

export default ContactListEditModal;
